import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ArrowSquareOut as OpenIcon } from "@phosphor-icons/react/ArrowSquareOut";
import { ArrowsClockwise as ArrowsClockwiseIcon } from "@phosphor-icons/react/ArrowsClockwise";
import { HourglassMedium as HourglassMediumIcon } from "@phosphor-icons/react/HourglassMedium";
import { Key as KeyIcon } from "@phosphor-icons/react/Key";
import { LockKey as LockKeyIcon } from "@phosphor-icons/react/LockKey";
import { Lifebuoy as LifebuoyIcon } from "@phosphor-icons/react/Lifebuoy";
import {
	BILLING_CYCLE_NAME,
	PLAN_NAME,
	SUBSCRIPTION_STATUS_COLOR,
	SUBSCRIPTION_STATUS_LABEL,
} from "../partner-templates/components/subscription-catalog";
import { QuotaHealthIndicator } from "../partner-templates/components/quota-health";
import type { Account } from "../partner-templates/types";
import { PortfolioRowActionMenu } from "./portfolio-row-action-menu";

export interface PortfolioTableProps {
	accounts: Account[];
	managingAccountId?: string | null;
	suspended?: boolean;
	onOpenAccount?: (id: string) => void;
	onManageAccount?: (id: string) => void;
	onOpenSubscription?: (id: string) => void;
	onRequestEndTrial?: (id: string) => void;
	onRequestCancelSubscription?: (id: string) => void;
	onRequestDeleteAccount?: (id: string) => void;
	onRestartTrial?: (id: string) => void;
}

function formatMoney(
	amount: { amountMinorUnits: number; currency: string } | null,
): string {
	if (!amount) return "—";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: amount.currency,
	}).format(amount.amountMinorUnits / 100);
}

function formatDate(value: string | null): string {
	if (!value) return "—";
	return new Date(value).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

export function PortfolioTable({
	accounts,
	managingAccountId = null,
	suspended = false,
	onOpenAccount,
	onManageAccount,
	onOpenSubscription,
	onRequestEndTrial,
	onRequestCancelSubscription,
	onRequestDeleteAccount,
	onRestartTrial,
}: PortfolioTableProps): React.JSX.Element {
	const hasPendingHandoff = (account: Account): boolean =>
		Boolean(account.isReverseHandoffInProgress);

	return (
		<Box sx={{ overflowX: "auto" }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Account</TableCell>
						<TableCell>Status</TableCell>
						<TableCell>Plan</TableCell>
						<TableCell>Cycle</TableCell>
						<TableCell>Period end</TableCell>
						<TableCell align="right">Recurring</TableCell>
						<TableCell>Quota</TableCell>
						<TableCell align="right">Actions</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{accounts.map((account) => {
						const isManaging = managingAccountId === account.id;
						const displayStatus =
							account.subscription.type === "TRIAL" ||
							account.subscription.type === "CLOSED"
								? account.subscription.type
								: account.isPastDue
									? "PAST_DUE"
									: "PAID";
						return (
							<TableRow
								key={account.id}
								hover>
								<TableCell>
									<Stack spacing={0.5}>
										<Stack
											direction="row"
											spacing={0.75}
											alignItems="center">
											<Link
												component="button"
												type="button"
												variant="body2"
												color="secondary"
												underline="hover"
												onClick={() => onOpenAccount?.(account.id)}
												sx={{
													fontWeight: 600,
													textAlign: "left",
												}}>
												{account.companyName}
											</Link>
											{account.mangomapSupportAccess && (
												<Tooltip title="MangoMap support access enabled">
													<Box
														component="span"
														sx={{
															color: "info.main",
															display: "inline-flex",
															alignItems: "center",
														}}>
														<LifebuoyIcon
															size={16}
															weight="duotone"
														/>
													</Box>
												</Tooltip>
											)}
										</Stack>
										<Stack
											direction="row"
											spacing={0.5}
											alignItems="center">
											{(() => {
												const invited = account.partnerActivationState
													? account.partnerActivationState !== "NOT_INVITED"
													: Boolean(
															!!account.clientOwner.cognitoId ||
																!!account.clientOwner.isActivated,
														);
												return (
													<Tooltip
														title={
															invited
																? "This client owner has login credentials."
																: "This client owner has no login credentials. Other client roles may still have access."
														}
														placement="top">
														<Box
															component="span"
															sx={{
																display: "inline-flex",
																alignItems: "center",
																color: invited
																	? "primary.main"
																	: "text.disabled",
															}}>
															{invited ? (
																<KeyIcon size={14} />
															) : (
																<LockKeyIcon size={14} />
															)}
														</Box>
													</Tooltip>
												);
											})()}
											<Typography
												variant="caption"
												color="text.secondary">
												{account.clientOwner.email}
											</Typography>
											{hasPendingHandoff(account) && (
												<Chip
													size="small"
													color="warning"
													variant="soft"
													label="Handoff pending"
												/>
											)}
										</Stack>
									</Stack>
								</TableCell>
								<TableCell>
									<Chip
										size="small"
										variant={displayStatus === "CLOSED" ? "outlined" : "soft"}
										color={SUBSCRIPTION_STATUS_COLOR[displayStatus]}
										label={SUBSCRIPTION_STATUS_LABEL[displayStatus]}
									/>
								</TableCell>
								<TableCell>
									<Typography variant="caption">
										{PLAN_NAME[account.subscription.paidConfig.plan] ??
											account.subscription.paidConfig.plan}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="caption">
										{BILLING_CYCLE_NAME[account.subscription.paidConfig.cycle]}
									</Typography>
								</TableCell>
								<TableCell>
									{account.subscription.type === "TRIAL" ? (
										<Stack
											direction="row"
											spacing={0.5}
											alignItems="center">
											<Tooltip title="Trial ends">
												<Box
													component="span"
													sx={{ display: "inline-flex", alignItems: "center" }}>
													<HourglassMediumIcon size={14} />
												</Box>
											</Tooltip>
											<Typography variant="caption">
												{formatDate(account.subscription.trialExpiresAt)}
											</Typography>
										</Stack>
									) : account.subscription.nextRenewalAt ? (
										<Stack
											direction="row"
											spacing={0.5}
											alignItems="center">
											<Tooltip title="Next renewal">
												<Box
													component="span"
													sx={{ display: "inline-flex", alignItems: "center" }}>
													<ArrowsClockwiseIcon size={14} />
												</Box>
											</Tooltip>
											<Typography variant="caption">
												{formatDate(account.subscription.nextRenewalAt)}
											</Typography>
										</Stack>
									) : (
										<Typography variant="caption">—</Typography>
									)}
								</TableCell>
								<TableCell align="right">
									<Typography variant="caption">
										{formatMoney(account.subscription.currentPeriodAmount)}
									</Typography>
								</TableCell>
								<TableCell>
									<QuotaHealthIndicator quota={account.quotaUsage} />
								</TableCell>
								<TableCell align="right">
									<Stack
										direction="row"
										spacing={0.5}
										justifyContent="flex-end">
										<Tooltip title="Open managed account session">
											<span>
												<Button
													size="small"
													variant="text"
													color="secondary"
													startIcon={
														isManaging ? (
															<CircularProgress size={14} />
														) : (
															<OpenIcon size={14} />
														)
													}
													onClick={() => onManageAccount?.(account.id)}
													disabled={isManaging}>
													{isManaging ? "Opening" : "Open account"}
												</Button>
											</span>
										</Tooltip>
										<PortfolioRowActionMenu
											account={account}
											suspended={suspended}
											onOpenAccount={onOpenAccount}
											onOpenSubscription={onOpenSubscription}
											onCancelSubscription={onRequestCancelSubscription}
											onDelete={onRequestDeleteAccount}
											onEndTrialEarly={onRequestEndTrial}
											onRestartTrial={onRestartTrial}
										/>
									</Stack>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</Box>
	);
}
